/**
 * «Actualizado hace N s» junto al título del tablero, con un punto que late mientras se piden datos
 * nuevos (el polling de 30 s nunca vuelve a mostrar esqueletos: este es todo su aviso).
 *
 * Solo este componente se vuelve a dibujar con el reloj (cada `TIC_MS`), no la página entera. No es una
 * región viva: anunciar la hora cada pocos segundos molestaría a quien usa lector de pantalla.
 */
import { useEffect, useState } from 'react';
import './IndicadorRefresco.css';

export const TIC_MS = 5000;

/** «hace un momento», «hace 25 s», «hace 3 min», «hace 2 h». Función pura (se prueba sin reloj). */
export function haceCuanto(desde: number, ahora: number): string {
  const segundos = Math.max(0, Math.floor((ahora - desde) / 1000));
  if (segundos < 5) return 'hace un momento';
  if (segundos < 60) return `hace ${segundos} s`;
  const minutos = Math.floor(segundos / 60);
  if (minutos < 60) return `hace ${minutos} min`;
  return `hace ${Math.floor(minutos / 60)} h`;
}

export function IndicadorRefresco({
  actualizadoEn,
  actualizando,
  fallo,
}: {
  /** `dataUpdatedAt` de la consulta (0 = aún sin datos: no se muestra nada). */
  actualizadoEn: number;
  actualizando: boolean;
  /** El último refresco falló (se siguen viendo los datos anteriores). */
  fallo: boolean;
}) {
  const [ahora, setAhora] = useState(() => Date.now());

  useEffect(() => {
    const temporizador = window.setInterval(() => setAhora(Date.now()), TIC_MS);
    return () => window.clearInterval(temporizador);
  }, []);

  if (!actualizadoEn) return null;
  const texto = actualizando
    ? 'Actualizando…'
    : fallo
      ? 'No se pudo actualizar'
      : `Actualizado ${haceCuanto(actualizadoEn, Math.max(ahora, actualizadoEn))}`;

  return (
    <span
      className={[
        'refresco',
        actualizando ? 'refresco--activo' : '',
        fallo && !actualizando ? 'refresco--fallo' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      title={new Date(actualizadoEn).toLocaleTimeString('es-CL')}
    >
      <span className="refresco__punto" aria-hidden="true" />
      {texto}
    </span>
  );
}
